import React, { useState, useEffect, useRef } from 'react';
import { Activity, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import api from '../lib/api';

// Compact live widget showing the proctoring recording upload queue health.
// Polls every 5s so admins can monitor recording uploads during big exam batches.
export const UploadQueueWidget = () => {
  const [stats, setStats] = useState(null);
  const timerRef = useRef(null);

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/proctoring/upload-queue/stats');
      setStats(res.data);
    } catch (e) {
      // silent — widget is best-effort
    }
  };

  useEffect(() => {
    fetchStats();
    timerRef.current = setInterval(fetchStats, 5000);
    return () => clearInterval(timerRef.current);
  }, []);

  if (!stats) return null;

  const depth = stats.queue_depth || 0;
  const failed = stats.failed_chunks || 0;
  const busy = depth > 0;
  const hasFailures = failed > 0;

  return (
    <div
      className="flex items-center gap-3 rounded-lg border bg-white px-3 py-2 text-xs shadow-sm"
      data-testid="upload-queue-widget"
      title="Live recording upload queue health"
    >
      <span className="flex items-center gap-1 font-semibold text-gray-700">
        <Activity className="w-3.5 h-3.5 text-blue-600" /> Recording Uploads
      </span>

      <span className="flex items-center gap-1" data-testid="queue-depth">
        {busy ? <Loader2 className="w-3.5 h-3.5 text-amber-500 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
        <span className={busy ? 'text-amber-600 font-medium' : 'text-gray-500'}>
          Queue: {depth}
        </span>
      </span>

      <span className="text-gray-400">|</span>

      <span className="text-gray-500" data-testid="queue-uploaded">Uploaded: {stats.uploaded || 0}</span>

      <span className="text-gray-400">|</span>

      <span
        className={`flex items-center gap-1 ${hasFailures ? 'text-red-600 font-semibold' : 'text-gray-500'}`}
        data-testid="queue-failed"
      >
        {hasFailures && <AlertTriangle className="w-3.5 h-3.5" />}
        Failed: {failed}
      </span>

      <span className="hidden md:inline-flex items-center gap-1 text-gray-400">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> {stats.workers} workers
      </span>
    </div>
  );
};
